import NumberInput from '../../../components/NumberInput'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X, Flame, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { useExerciseHistory } from '../../../hooks/useExerciseHistory'
import ExercisePlaceholder from './ExercisePlaceholder'
import ImagePreviewDialog from '../../../components/ImagePreviewDialog'

// Compact M/D rendering for the recent-sessions strip. Avoids
// pulling in date-fns just to format "May 28"; the strip stays
// readable in either locale because numerics are direction-neutral
// inside the dir="ltr" span we render them in.
function formatHistoryDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso + 'T00:00:00')
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return iso }
}

export default function SetLogger({ log, exercise, onChange, onRemove, onMoveUp, onMoveDown }) {
  const { t } = useTranslation()
  const { lastSet, lastSets, recentSessions } = useExerciseHistory(log.exerciseId)
  const [previewOpen, setPreviewOpen] = useState(false)

  // Seed a new set from last session's set at the same index — so
  // the user can replay set 1 → set 2 → set 3 without retyping. If
  // last session had fewer sets, fall back to the last available
  // (so an extra set still gets a reasonable seed) and ultimately
  // to the previous in-progress set or the overall lastSet headline.
  const addSet = () => {
    const idx = log.sets.length
    const prev = log.sets[log.sets.length - 1]
    const historical = lastSets[idx] || lastSets[lastSets.length - 1] || null
    let seed
    if (historical) {
      seed = {
        weight_kg: String(historical.weight_kg),
        reps: String(historical.reps),
        rpe: '',
        is_warmup: false,
        is_failure: false,
      }
    } else if (prev) {
      seed = {
        weight_kg: prev.weight_kg,
        reps: prev.reps,
        rpe: prev.rpe,
        is_warmup: false,
        is_failure: false,
      }
    } else if (lastSet) {
      seed = {
        weight_kg: String(lastSet.weight_kg),
        reps: String(lastSet.reps),
        rpe: '',
        is_warmup: false,
        is_failure: false,
      }
    } else {
      seed = { weight_kg: '', reps: '', rpe: '', is_warmup: false, is_failure: false }
    }
    onChange({ sets: [...log.sets, seed] })
  }

  const updateSet = (idx, patch) => {
    onChange({ sets: log.sets.map((s, i) => (i === idx ? { ...s, ...patch } : s)) })
  }

  const removeSet = (idx) => {
    onChange({ sets: log.sets.filter((_, i) => i !== idx) })
  }

  const exerciseName = exercise?.name || t('workout.session.missingExercise')

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-start gap-3">
          {/* Thumbnail — uploaded photo if there is one, otherwise the
              muscle-group placeholder. bg-white renders transparent PNGs
              against a known surface so the illustration stays visible
              on the dark theme. Tappable when a real photo exists →
              opens a full-size preview dialog. */}
          {exercise?.photoUrl ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              aria-label={t('workout.session.previewExercise', { name: exerciseName })}
              className="w-14 h-14 rounded-md overflow-hidden bg-white flex-shrink-0 border border-border hover:border-app-primary-500/60 transition-colors"
            >
              <img
                src={exercise.photoUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="w-14 h-14 rounded-md overflow-hidden bg-white flex-shrink-0 border border-border">
              <ExercisePlaceholder
                muscleGroup={exercise?.muscle_group}
                iconClassName="w-6 h-6"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate" dir="auto">
              {exerciseName}
            </p>
            <p className="text-xs text-muted-foreground" dir="auto">
              {lastSet
                ? t('workout.session.lastSet', { weight: lastSet.weight_kg, reps: lastSet.reps })
                : t('workout.session.noHistory')}
            </p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={onMoveUp || undefined}
              disabled={!onMoveUp}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground p-1.5"
              aria-label={t('workout.session.moveUp')}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onMoveDown || undefined}
              disabled={!onMoveDown}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground p-1.5"
              aria-label={t('workout.session.moveDown')}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="text-muted-foreground hover:text-red-500 p-1.5"
              aria-label={t('workout.session.removeExercise')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {recentSessions.length > 0 && (
          <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground" dir="auto">
              {t('workout.session.recentSessions')}
            </p>
            <div className="space-y-0.5">
              {recentSessions.map((sess, i) => (
                <div
                  key={`${sess.date}-${i}`}
                  className="flex items-center gap-2 text-[11px] text-foreground"
                  dir="auto"
                >
                  <span className="text-muted-foreground font-mono tabular-nums text-[10px] flex-shrink-0 w-16" dir="ltr">
                    {formatHistoryDate(sess.date)}
                  </span>
                  <span className="flex-1 truncate font-mono tabular-nums" dir="ltr">
                    {sess.sets.map((s) => `${s.weight_kg}×${s.reps}`).join('  ·  ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {log.sets.map((s, idx) => {
            const historical = lastSets[idx]
            return (
            <div key={idx} className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="w-6 text-xs text-muted-foreground text-center flex-shrink-0">
                {idx + 1}
              </span>
              <NumberInput
                inputMode="decimal"
                step="0.5"
                value={s.weight_kg}
                onChange={(e) => updateSet(idx, { weight_kg: e.target.value })}
                placeholder={historical ? String(historical.weight_kg) : t('workout.set.weight')}
                className="h-10 flex-1 min-w-0 px-2 rounded-md border border-input bg-background text-foreground text-base sm:text-sm text-center"
                dir="ltr"
                aria-label={t('workout.set.weight')}
              />
              <NumberInput
                inputMode="numeric"
                step="1"
                value={s.reps}
                onChange={(e) => updateSet(idx, { reps: e.target.value })}
                placeholder={historical ? String(historical.reps) : t('workout.set.reps')}
                className="h-10 flex-1 min-w-0 px-2 rounded-md border border-input bg-background text-foreground text-base sm:text-sm text-center"
                dir="ltr"
                aria-label={t('workout.set.reps')}
              />
              <button
                type="button"
                onClick={() => updateSet(idx, { is_warmup: !s.is_warmup })}
                className={cn(
                  'h-10 w-10 flex items-center justify-center rounded-md border text-xs font-medium flex-shrink-0',
                  s.is_warmup
                    ? 'border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'border-input text-muted-foreground'
                )}
                title={t('workout.set.warmup')}
                aria-label={t('workout.set.warmup')}
                aria-pressed={!!s.is_warmup}
              >
                W
              </button>
              <button
                type="button"
                onClick={() => updateSet(idx, { is_failure: !s.is_failure })}
                className={cn(
                  'h-10 w-10 flex items-center justify-center rounded-md border flex-shrink-0',
                  s.is_failure
                    ? 'border-red-400 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'border-input text-muted-foreground'
                )}
                title={t('workout.set.failure')}
                aria-label={t('workout.set.failure')}
                aria-pressed={!!s.is_failure}
              >
                <Flame className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => removeSet(idx)}
                className="h-10 w-10 flex items-center justify-center text-muted-foreground hover:text-red-500 flex-shrink-0"
                aria-label={t('workout.set.remove')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {historical && (
              <p
                className="text-[10px] text-muted-foreground/80 ps-7"
                dir="ltr"
              >
                {t('workout.session.lastSet', { weight: historical.weight_kg, reps: historical.reps })}
              </p>
            )}
            </div>
            )
          })}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addSet}
          size="sm"
          className="w-full"
        >
          <Plus className="w-4 h-4 me-1.5" />
          {t('workout.set.add')}
        </Button>

        <textarea
          value={log.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder={t('workout.session.exerciseNotesPlaceholder')}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
          rows={2}
          aria-label={t('workout.session.exerciseNotes')}
        />
      </CardContent>

      <ImagePreviewDialog
        open={previewOpen}
        src={exercise?.photoUrl}
        alt={exerciseName}
        onClose={() => setPreviewOpen(false)}
      />
    </Card>
  )
}
